import { Injectable } from '@angular/core';

import { Project } from '../models/project';
import { TimeDateUtils } from './date-utils';
import { isOfficialLeaveCell } from '../services/official-leave.service';

export interface ClipboardCell {
  hours: string;
  notes: string;
}

export interface TimesheetClipboardContext {
  getWeekStart(): Date | null | undefined;
  getProjects(): Project[];
  afterChange(): void;
}

@Injectable()
export class TimesheetClipboardService {
  readonly rowClipboard: ClipboardCell[] = [];
  readonly colClipboard: ClipboardCell[] = [];

  private context?: TimesheetClipboardContext;

  configure(context: TimesheetClipboardContext): void {
    this.context = context;
  }

  copyRow(project: Project, isCut: boolean = false): void {
    const weekStart = this.context?.getWeekStart();
    if (!weekStart) return;

    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      const dayKey = TimeDateUtils.getDay(day);
      this.rowClipboard[i] = {
        hours: project.hours[dayKey],
        notes: project.notes[dayKey],
      };

      if (isCut && !isOfficialLeaveCell(project, dayKey)) {
        project.hours[dayKey] = '';
        project.notes[dayKey] = '';
      }
    }

    if (isCut) this.context?.afterChange();
  }

  cutRow(project: Project): void {
    this.copyRow(project, true);
  }

  pasteRow(project: Project): void {
    const weekStart = this.context?.getWeekStart();
    if (!weekStart) return;

    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      const dayKey = TimeDateUtils.getDay(day);
      const source = this.rowClipboard[i];
      if (!source || isOfficialLeaveCell(project, dayKey)) continue;

      project.hours[dayKey] = source.hours;
      project.notes[dayKey] = source.notes;
    }

    this.context?.afterChange();
  }

  copyCol(dayKey: string, isCut: boolean = false): void {
    if (!this.context?.getWeekStart()) return;

    const projects = this.context.getProjects();
    for (let i = 0; i < projects.length; i++) {
      const project = projects[i];
      this.colClipboard[i] = {
        hours: project.hours[dayKey],
        notes: project.notes[dayKey],
      };

      if (isCut && !isOfficialLeaveCell(project, dayKey)) {
        project.hours[dayKey] = '';
        project.notes[dayKey] = '';
      }
    }

    if (isCut) this.context.afterChange();
  }

  cutCol(dayKey: string): void {
    this.copyCol(dayKey, true);
  }

  pasteCol(dayKey: string): void {
    if (!this.context?.getWeekStart()) return;

    const projects = this.context.getProjects();
    for (let i = 0; i < projects.length; i++) {
      const source = this.colClipboard[i];
      if (!source || isOfficialLeaveCell(projects[i], dayKey)) continue;

      projects[i].hours[dayKey] = source.hours;
      projects[i].notes[dayKey] = source.notes;
    }

    this.context.afterChange();
  }
}
